/**
 * Action registry for Information Request list hover toolbar and detail overflow menu.
 * @see docs/NXTGEN-5875/information-request-redesign-plan.md — Action matrix
 * @see docs/NXTGEN-5875/02-hover-actions-by-status.md
 */

import { canDownloadAttachments } from './requestDetail';

export const REQUEST_ACTION_IDS = {
  EDIT_REQUEST: 'edit-request',
  NOTIFY: 'notify',
  APPROVE: 'approve',
  REJECT: 'reject',
  DOWNLOAD: 'download',
  DOWNLOAD_ALL: 'download-all',
  CANCEL: 'cancel',
  DELETE: 'delete',
  RESTORE: 'restore',
};

const STAGE = {
  PENDING_SUBMISSION: 0,
  PENDING_APPROVAL: 1,
  APPROVED: 2,
  CANCELLED: -1,
};

const ICONS = {
  EDIT: '/img/icons/fbc-icons/CycleMenuEdit.svg',
  NOTIFY: '/img/icons/fbc-icons/NotifyIcon.svg',
  APPROVE: '/img/icons/fbc-icons/ApproveIcon.svg',
  REJECT: '/img/icons/fbc-icons/RejectIcon.svg',
  DOWNLOAD: '/img/icons/fbc-icons/DownloadIcon.svg',
  CANCEL: '/img/icons/fbc-icons/CancelRequest.svg',
  DELETE: '/img/icons/fbc-icons/DeleteActions.svg',
};

/**
 * @typedef {'list'|'detail'} RequestActionSurface
 */

/**
 * @typedef {Object} RequestAction
 * @property {string} id
 * @property {'primary'|'overflow'} placement
 * @property {string} [label]
 * @property {string} [icon]
 */

/**
 * Returns primary (inline) and overflow actions for a request row or detail header.
 *
 * @param {Object} request - Information request from Vuex.
 * @param {Object} [ctx]
 * @param {RequestActionSurface} [ctx.surface='list'] - `notify` is list-only.
 * @param {boolean} [ctx.canEdit=true]
 * @param {boolean} [ctx.canDelete=true]
 * @returns {{ primary: RequestAction[], overflow: RequestAction[] }}
 */
export function getRequestActions(request, ctx = {}) {
  const {
    surface = 'list',
    canEdit = true,
    canDelete = true,
  } = ctx;

  const stage = request?.stage;
  const primary = [];
  const overflow = [];

  const pushPrimary = (id, label, icon, opts = {}) => {
    primary.push({
      id, placement: 'primary', label, icon, ...opts,
    });
  };
  const pushOverflow = (id, label, icon) => overflow.push({ id, placement: 'overflow', label, icon });

  switch (stage) {
    case STAGE.PENDING_SUBMISSION:
      // Figma Hover Pending Sub (2986:174037): Edit → Notify → ⋮
      if (canEdit) pushPrimary(REQUEST_ACTION_IDS.EDIT_REQUEST, 'Edit request', ICONS.EDIT);
      if (surface === 'list') pushPrimary(REQUEST_ACTION_IDS.NOTIFY, 'Notify', ICONS.NOTIFY);
      pushOverflow(REQUEST_ACTION_IDS.CANCEL, 'Cancel request', ICONS.CANCEL);
      if (canDelete) pushOverflow(REQUEST_ACTION_IDS.DELETE, 'Delete request', ICONS.DELETE);
      break;

    case STAGE.PENDING_APPROVAL:
      if (surface === 'list') {
        // Figma Pending Approval toolbar (2986:174120): Approve → Reject → ⋮
        pushPrimary(REQUEST_ACTION_IDS.APPROVE, 'Approve', ICONS.APPROVE);
        pushPrimary(REQUEST_ACTION_IDS.REJECT, 'Reject', ICONS.REJECT);
        if (canDownloadAttachments(request)) {
          pushOverflow(REQUEST_ACTION_IDS.DOWNLOAD_ALL, 'Export', ICONS.DOWNLOAD);
        }
      } else {
        // Figma detail header: Reject → Approve → Download (icon) → ⋮
        pushPrimary(REQUEST_ACTION_IDS.REJECT, 'Reject', 'mdi-close');
        pushPrimary(REQUEST_ACTION_IDS.APPROVE, 'Approve', 'mdi-check-circle-outline');
        if (canDownloadAttachments(request)) {
          pushPrimary(
            REQUEST_ACTION_IDS.DOWNLOAD,
            'Download',
            ICONS.DOWNLOAD,
            { iconOnly: true },
          );
        }
      }
      pushOverflow(REQUEST_ACTION_IDS.CANCEL, 'Cancel request', ICONS.CANCEL);
      if (canDelete) pushOverflow(REQUEST_ACTION_IDS.DELETE, 'Delete request', ICONS.DELETE);
      break;

    case STAGE.APPROVED:
      if (surface === 'detail') {
        // Detail header: icon-only download when files exist; no Export in overflow
        if (canDownloadAttachments(request)) {
          pushPrimary(
            REQUEST_ACTION_IDS.DOWNLOAD,
            'Download',
            ICONS.DOWNLOAD,
            { iconOnly: true },
          );
        }
      } else {
        // List: Download only — approved requests are not editable from the table hover toolbar.
        if (canDownloadAttachments(request)) {
          pushPrimary(REQUEST_ACTION_IDS.DOWNLOAD, 'Download', ICONS.DOWNLOAD);
          pushOverflow(REQUEST_ACTION_IDS.DOWNLOAD_ALL, 'Export', ICONS.DOWNLOAD);
        }
      }
      if (canDelete) pushOverflow(REQUEST_ACTION_IDS.DELETE, 'Delete request', ICONS.DELETE);
      break;

    case STAGE.CANCELLED:
      pushPrimary(REQUEST_ACTION_IDS.RESTORE, 'Restore', 'mdi-restore');
      if (canDelete) pushOverflow(REQUEST_ACTION_IDS.DELETE, 'Delete request', ICONS.DELETE);
      break;

    default:
      break;
  }

  return { primary, overflow };
}

/**
 * Flat list of all actions (primary first, then overflow).
 * @param {Object} request
 * @param {Object} [ctx]
 * @returns {RequestAction[]}
 */
export function getAllRequestActions(request, ctx = {}) {
  const { primary, overflow } = getRequestActions(request, ctx);
  return [...primary, ...overflow];
}
